/**
 * Riser configuration model + layout generation.
 *
 * A "Risers" element (iconId: 'risers') is a SINGLE stage element whose
 * contents are described by `RiserConfig` stored in StageElement.config.
 * It represents a row of stage risers (side-fills / platforms) the user
 * sizes and counts. The on-stage graphic draws a top-down row of
 * rectangular risers with their dimensions labelled.
 *
 * Coordinates: the graphic is drawn in its own 0..100 x 0..100 box and
 * scaled to the element's pixel size on the canvas. Risers are laid out
 * left→right as a single row (typical for a backline riser line).
 */

import { STAGE_SVG_GRAY } from './StageIcon';

export interface RiserDef {
  /** Width in cm (or any unit — kept numeric for display). */
  width: number;
  /** Depth (front-to-back) in cm. */
  depth: number;
  /** Height of the platform in cm. */
  height: number;
}

export interface RiserConfig {
  /** Number of riser blocks in the row. */
  count: number;
  /** Whether all risers share one size (true) or are individually listed. */
  uniform: boolean;
  /** Shared size when uniform=true. */
  size: RiserDef;
  /** Per-riser sizes when uniform=false (length should match count). */
  items: RiserDef[];
  /** Whether the risers sit on wheels (caster-mounted platforms). */
  onWheels: boolean;
}

export function defaultRisers(): RiserConfig {
  return {
    count: 1,
    uniform: true,
    size: { width: 100, depth: 200, height: 40 },
    items: [],
    onWheels: false,
  };
}

const CLAMP = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

export function cloneRisers(c: RiserConfig): RiserConfig {
  return {
    count: c.count,
    uniform: c.uniform,
    size: { ...c.size },
    items: c.items.map((r) => ({ ...r })),
    onWheels: !!c.onWheels,
  };
}

export function riserItems(c: RiserConfig): RiserDef[] {
  if (c.uniform) {
    return Array.from({ length: CLAMP(c.count, 1, 12) }, () => ({ ...c.size }));
  }
  const n = CLAMP(c.count, 1, 12);
  const out: RiserDef[] = [];
  for (let i = 0; i < n; i++) out.push(c.items[i] ? { ...c.items[i] } : { ...c.size });
  return out;
}

/** Normalised layout: each riser as a rect in real W×D space (single row). */
export interface RiserRect {
  x: number;
  y: number;
  w: number;
  h: number;
  /** index for labelling */
  i: number;
}

/** Shared dimension math so layout / viewBox / footprint never diverge. */
function riserDims(cfg: RiserConfig): { blockW: number; maxD: number; padExt: number; totalW: number; totalH: number; n: number } | null {
  const items = riserItems(cfg);
  const n = items.length;
  if (n === 0) return null;
  const padExt = 4; // cm, outer margin (keeps edge stroke from clipping)
  const blockW = Math.max(20, items[0].width);
  const maxD = Math.max(20, ...items.map((r) => Math.max(20, r.depth)));
  return {
    blockW,
    maxD,
    padExt,
    n,
    totalW: blockW * n + padExt * 2,
    totalH: maxD + padExt * 2,
  };
}

export function layoutRisers(cfg: RiserConfig): RiserRect[] {
  const dims = riserDims(cfg);
  if (!dims) return [];
  const { blockW, maxD, padExt, n, totalH } = dims;
  // Risers are BUTTED together (no inter-block gap) — only a thin outer margin.
  const rowH = maxD;
  const y = padExt + (totalH - padExt * 2 - rowH) / 2;
  let x = padExt;
  const rects: RiserRect[] = [];
  for (let i = 0; i < n; i++) {
    rects.push({
      x,
      y,
      w: blockW,
      h: rowH,
      i,
    });
    x += blockW;
  }
  return rects;
}

/** Real W×D viewBox dimensions for the layout (so the graphic keeps its ratio). */
export function riserViewBox(cfg: RiserConfig): { w: number; h: number } {
  const dims = riserDims(cfg);
  if (!dims) return { w: 100, h: 100 };
  return { w: dims.totalW, h: dims.totalH };
}

/**
 * Real on-stage footprint of a riser element, in METERS (so the StageElement
 * width/height match the actual footprint). Returns null if the config is
 * empty (caller keeps the current size).
 *
 * This reports the REAL footprint (butted blocks: blockW × count wide, maxD
 * deep) WITHOUT the 4cm stroke margin (padExt) that only exists in the
 * viewBox to keep edge strokes from clipping. The on-canvas element box and
 * any dimension readout therefore match exactly what the user entered in the
 * riser editor (e.g. a 3m×2m riser → 3m × 2m, not 3.08m × 2.08m).
 */
export function riserFootprint(cfg: RiserConfig | null | undefined): { width: number; height: number } | null {
  if (!cfg || typeof cfg !== 'object' || typeof cfg.count !== 'number') return null;
  const items = riserItems(cfg);
  const n = items.length;
  if (n === 0) return null;
  const blockW = Math.max(20, items[0].width);
  const maxD = Math.max(20, ...items.map((r) => Math.max(20, r.depth)));
  return {
    width: Math.round(((blockW * n) / 100) * 100) / 100,
    height: Math.round((maxD / 100) * 100) / 100,
  };
}

/**
 * Single-riser block spec, e.g. "200 cm × 200 cm × 20 cm". Uses the first
 * block's dimensions (representative for a uniform riser or the lead block).
 */
function riserBlockSpec(def: RiserDef): string {
  return `${def.width} cm × ${def.depth} cm × ${def.height} cm`;
}

/**
 * Total spec shown under a riser on the stage: the block dimensions plus
 * the wheel flag. The riser COUNT is always prefixed so the number of
 * identical platforms is visible, e.g.
 * "3 × 200 cm × 100 cm × 40 cm, on wheels". Mirrors the example
 * "200 cm × 200 cm × 20 cm, on wheels" (now with the leading count).
 */
export function riserTotalSpec(cfg: RiserConfig | null | undefined): string {
  if (!cfg || typeof cfg !== 'object' || typeof cfg.count !== 'number') return '';
  const items = riserItems(cfg);
  if (items.length === 0) return '';
  const first = items[0];
  const head = `${items.length} × ${riserBlockSpec(first)}`;
  const wheels = cfg.onWheels ? ', on wheels' : '';
  return `${head}${wheels}`;
}

export function riserSummary(cfg: RiserConfig): string {
  const items = riserItems(cfg);
  if (items.length === 0) return '0 risers';
  if (cfg.uniform) {
    const s = cfg.size;
    return `${items.length}× ${s.width}×${s.depth}×${s.height}${cfg.onWheels ? ', on wheels' : ''}`;
  }
  return `${items.length} risers${cfg.onWheels ? ', on wheels' : ''}`;
}

/**
 * Serialise a RiserConfig to an SVG STRING (viewBox 0 0 100 100) for the
 * PNG export path.
 */
export function riserToSvg(cfg: RiserConfig | null | undefined, _color: string): string {
  const safe = cfg && typeof cfg === 'object' && typeof cfg.count === 'number' ? cfg : defaultRisers();
  const rects = layoutRisers(safe);
  const vb = riserViewBox(safe);
  const parts: string[] = [];
  for (let idx = 0; idx < rects.length; idx++) {
    const r = rects[idx];
    parts.push(
      `<rect x="${r.x}" y="${r.y}" width="${r.w}" height="${r.h}" rx="2" fill="${STAGE_SVG_GRAY}" stroke="#000000" stroke-width="1.5"/>`,
    );
    parts.push(
      `<rect x="${r.x}" y="${r.y}" width="${r.w}" height="${Math.min(4, r.h * 0.3)}" rx="2" fill="#00000030"/>`,
    );
    // Vertical divider on the right edge of every block except the last (risers are butted).
    if (idx < rects.length - 1) {
      parts.push(
        `<line x1="${r.x + r.w}" y1="${r.y}" x2="${r.x + r.w}" y2="${r.y + r.h}" stroke="#000000" stroke-width="1.5"/>`,
      );
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}">${parts.join('')}</svg>`;
}
